// ═══════════════════════════════════════════════════════════════════════════
// Connect — page assembler.
//
//   ┌── header: icon · "Connect" · app-presence pill ─────────────────────────┐
//   ├── left panel ─────────────────────────┐  ┌── right rail ───────────────┐
//   │  app closed     → InstallPanel        │  │  StepsRail (3 steps +       │
//   │  app signed out → SignedOutPanel      │  │  state-dependent footer)    │
//   │  app signed in  → ProtectedDevices    │  │                             │
//   └───────────────────────────────────────┘  └─────────────────────────────┘
//
// App presence comes from the local desktop daemon (127.0.0.1:7979) via
// DaemonContext, which reports three distinct states:
//   offline           → app is closed        → install screen
//   not-authenticated → app is open, no user → signed-out screen (NOT install:
//                                               the app was found, so telling
//                                               the user to download it is wrong)
//   ready             → app is open + signed in → device screen
// While the app isn't ready we re-probe on an interval and whenever the tab
// regains focus, so opening or signing in flips the page over without a refresh.
//
// The device panel shows this machine only, from live daemon data. Listing a
// user's other enrolled devices needs an enterprise-backend endpoint that does
// not exist yet (see ProtectedDevicesPanel).
// ═══════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import { useDaemon } from "../../context/DaemonContext";
import useHandoffLogin from "./useHandoffLogin";
import styles from "./Connect.module.css";

import ConnectHeader from "./components/ConnectHeader";
import ProtectedDevicesPanel from "./components/ProtectedDevicesPanel";
import SignedOutPanel from "./components/SignedOutPanel";
import WrongUserPanel from "./components/WrongUserPanel";
import InstallPanel from "./components/InstallPanel";
import StepsRail from "./components/StepsRail";

// How often to re-probe the daemon while the app is not detected.
const PROBE_INTERVAL_MS = 5000;

export default function Connect() {
  const { daemonStatus, recheckDaemon } = useDaemon();

  // One hand-off per page, not one per button. The left panel and the rail both
  // offer "sign the app in", and a code minted by the second click invalidates
  // the first — so they share a single in-flight flag and a single error.
  const handoff = useHandoffLogin();

  // Two independent facts, kept separate on purpose:
  //   appPresent — the daemon answered /health, so the app is running
  //   signedIn   — ...and /whoami reported a logged-in account
  // Collapsing these into one flag is what used to show "we couldn't find the
  // app" to people whose app was running fine, just signed out.
  const signedIn   = daemonStatus === "ready";
  const wrongUser  = daemonStatus === "wrong-user";
  const appPresent = signedIn || wrongUser || daemonStatus === "not-authenticated";

  // Manual override: the rail lets a user open the install flow even when their
  // own app is already running (to install it on another device).
  const [showInstall, setShowInstall] = useState(false);
  const showDevices   = signedIn && !showInstall;
  const showWrongUser = wrongUser && !showInstall;
  const showSignedOut = appPresent && !signedIn && !wrongUser && !showInstall;
  const showInstallScreen = !showDevices && !showSignedOut && !showWrongUser;
  const railMode = showDevices
    ? "devices"
    : showWrongUser
      ? "wrongUser"
      : showSignedOut
        ? "signedOut"
        : "install";

  // Keep probing until the app is fully usable — on an interval and on tab
  // focus. Note this keeps running in the signed-out state so that signing in
  // inside the app flips this page over on its own.
  useEffect(() => {
    if (signedIn) return undefined;

    const timer = setInterval(recheckDaemon, PROBE_INTERVAL_MS);
    const onFocus = () => recheckDaemon();
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [signedIn, recheckDaemon]);

  return (
    <div className={styles.page}>
      <ConnectHeader daemonStatus={daemonStatus} />

      {showInstallScreen && (
        <div className={styles.intro}>
          <h2 className={styles.introTitle}>Install PQ VPN to protect your device.</h2>
          <p className={styles.introSubtitle}>
            {showInstall
              ? "Choose where you want to install it."
              : "We couldn't find the PQ VPN app. Choose where you want to install it."}
          </p>
        </div>
      )}

      <div className={styles.body}>
        {showDevices && <ProtectedDevicesPanel />}
        {showWrongUser && <WrongUserPanel handoff={handoff} />}
        {showSignedOut && <SignedOutPanel handoff={handoff} />}
        {showInstallScreen && <InstallPanel />}

        <StepsRail
          mode={railMode}
          handoff={handoff}
          onInstallElsewhere={() => setShowInstall(true)}
          onRetry={() => { setShowInstall(false); recheckDaemon(); }}
        />
      </div>
    </div>
  );
}
